import { Injectable } from '@angular/core';
import {
  Action,
  createModelSelector,
  createPropertySelectors,
  State,
  StateContext,
  Store,
} from '@ngxs/store';
import { finalize } from 'rxjs';
import { MemoService } from 'src/app/modules/memos/service/memo.service';
import {
  Approve,
  CancelDecisionPopup,
  ClearSelectMemo,
  ClearSignatureDraft,
  ClearState,
  ClosePreviewData,
  EmptyContent,
  InitialMemoDetail,
  MultiApprove,
  MultiReject,
  MultiTerminate,
  NextApprovalPopup,
  Reject,
  SaveComment,
  SaveConfirm,
  SaveFormDataAndStatus,
  SaveLocation,
  SaveOTP,
  SaveReason,
  SaveSignature,
  SaveStamp,
  SetFromClickMemo,
  ShowDecisionPopup,
  ShowMemoDetail,
  ShowPreviewData,
  Terminate,
  ToggleMenu,
  UpdateMultiMemos,
  SetIsSelectAll,
  MultiApproveForS11Document,
  MultiTerminateS11Document,
  UpdateMultiSelectedCount,
  SetMultiApproveS11Done,
  SaveListQueryParams,
} from './approval-request.actions';
import {
  ApprovalRequestList,
  ApprovalRequestStateModel,
  DecitionPopup,
  MemoAction,
  PaymentData,
  PreviewData,
} from './approval-request.models';
import { SpinnerService } from '@core/services/spinner.service';
import { ErrorNotification } from '../memo/memo.actions';
import { MemoDetail } from 'src/app/modules/memos/model/memo.model';
import { NotificationService } from '@shared/service/notification.service';
import { AlertService } from '@core/services/alert.service';
import { append, patch, updateItem } from '@ngxs/store/operators';
import { SidebarService } from '@core/components/sidebar/sidebar.service';
import { PreApproveService } from 'src/app/modules/s11-e-signature/shared/pre-approve.service';
import { Router } from '@angular/router';
import { SetOnApprove } from '@store/pre-approve/pre-approve.actions';
import { SetBackgroundTask } from './approval-request.actions';
import { AuthenticationService } from '../../core/authentication/authentication.service';
import { OtpSmsService } from 'src/app/modules/profile/component/setting-otp-sms/otp-sms.service';

const defaultData = {
  memo: null,
  viaLink: false,
  selectedMemoIds: [],
  showingMenu: true,
  emptyContent: true,
  showDecisionPopup: null,
  passedDecisionPopups: [],
  nextApprovalPopup: null,
  actionPayload: {
    action: null,
    comments: [],
    reason: null,
    ref: null,
    otp: null,
    signature: null,
    stamp: [],
    negotiate: null,
  },
  previewData: {
    preview: false,
    previewDataList: [],
    previewData: null,
    header: '',
  },
  multiMemos: [],
  exceedMaximumApprove: false,
  multiApprovalTaskId: null,
  multiApprovalTaskTitle: null,
  selectedType: null,
  statusFetch: null,
  s11ContractData: null,
  s11ContractFormValid: null,
  fromClickMemo: false,
  isSelectAll: false,
  selectAllMemoType: null,
  multiSelectedCount: 0,
  multiApproveS11Done: false,
  multiS11ActionType: null,
  listQueryParams: null,
};

@State<ApprovalRequestStateModel>({
  name: 'approvalRequest',
  defaults: defaultData,
})
@Injectable({
  providedIn: 'root',
})
export class ApprovalRequestState {
  private location: {
    latitude: number;
    longitude: number;
  };

  constructor(
    private memoService: MemoService,
    private spinnerService: SpinnerService,
    private store: Store,
    public notificationService: NotificationService,
    private sidebarService: SidebarService,
    private preApproveService: PreApproveService,
    private router: Router,
    private authenticationService: AuthenticationService,
    private alert: AlertService,
    private otpSmsService: OtpSmsService,
  ) {}

  @Action(ShowMemoDetail)
  showMemoDetail(
    { setState, getState }: StateContext<ApprovalRequestStateModel>,
    payload: ShowMemoDetail,
  ) {
    this.spinnerService.show();

    const memoType = payload.options?.memo_type;
    let getMemoDetail = [
      'pre_approve',
      's11_contract',
      'other_reference',
    ].includes(memoType as string)
      ? this.memoService.getMemoDetailByType(
          payload.memoId,
          payload.options?.memo_type,
        )
      : this.memoService.getMemoDetail(payload.memoId);
    if (
      payload.viaLink &&
      payload?.options?.memo_type === 'contract'
    ) {
      getMemoDetail = this.memoService.getContractDetail(
        payload.memoId,
        payload?.options?.otp || '',
        payload?.options?.ref || '',
      );
    }
    getMemoDetail
      .pipe(
        finalize(() => {
          this.spinnerService.hide();
        }),
      )
      .subscribe(
        (res) => {
          const viaLink = payload.viaLink || false;
          this.store.dispatch(new InitialMemoDetail(res, viaLink));
        },
        (error: any) => {
          if (
            error.status === 403 &&
            ['REQT_INVALID_PASSWORD', 'INVALID_PASSWORD'].includes(
              error.error.detail,
            )
          ) {
            const state = getState();
            this.store.dispatch(new ClearState());
            this.store.dispatch(new EmptyContent(state.emptyContent));
          } else if (
            ['invalid_otp', 'missing_otp'].includes(error.error?.code)
          ) {
            const state = getState();
            this.store.dispatch(new ClearState());
            this.store.dispatch(new EmptyContent(state.emptyContent));
            if (payload?.options?.onVerifyError) {
              payload.options.onVerifyError(error);
            }
          } else {
            this.store.dispatch(new ErrorNotification(error));
            this.router.navigate(['/']);
          }
        },
      );
  }

  @Action(InitialMemoDetail)
  initialMemoDetail(
    { setState, getState }: StateContext<ApprovalRequestStateModel>,
    payload: InitialMemoDetail,
  ) {
    const state = getState();
    const viaLink = payload.viaLink || false;
    setState({
      ...state,
      memo: payload.detail,
      viaLink: viaLink,
      showDecisionPopup: null,
      passedDecisionPopups: [],
      nextApprovalPopup: null,
      emptyContent: false,
      actionPayload: {
        action: null,
        comments: [],
        reason: null,
        ref: null,
        otp: null,
        signature: null,
        stamp: [],
        negotiate: null,
      },
    });
  }

  @Action(SetBackgroundTask)
  setBackgroundTask(
    { patchState }: StateContext<ApprovalRequestStateModel>,
    { taskId, title }: SetBackgroundTask,
  ) {
    patchState({
      multiApprovalTaskId: taskId,
      multiApprovalTaskTitle: title ?? null,
    });
  }

  @Action(ClearSelectMemo)
  clearSelectMemo(
    { getState, patchState }: StateContext<ApprovalRequestStateModel>,
    { showingMenu }: { showingMenu: boolean },
  ) {
    let _showingMenu = getState().showingMenu;
    if (showingMenu) {
      _showingMenu = showingMenu;
    }
    patchState({
      memo: null,
      showingMenu: _showingMenu,
    });
  }

  @Action(ToggleMenu)
  toggleMenu({
    patchState,
    getState,
  }: StateContext<ApprovalRequestStateModel>) {
    const state = getState();
    patchState({
      showingMenu: !state.showingMenu,
    });
  }

  @Action(ShowDecisionPopup)
  showDecisionPopup(
    { getState, setState }: StateContext<ApprovalRequestStateModel>,
    payload?: { action?: MemoAction; options?: { is_user: boolean } },
  ) {
    const state = getState();
    const memo = state.memo;
    const multiMemos = state.multiMemos;
    if (!memo && !multiMemos.length) {
      return;
    }
    const memos = multiMemos.length > 0 ? multiMemos : [memo];

    const actionPayload = state.actionPayload;
    if (payload?.action) {
      actionPayload.action = payload.action;
    }
    const action = actionPayload.action;
    if (!action) {
      return;
    }

    const pass = (popup: DecitionPopup): boolean => {
      return state.passedDecisionPopups.includes(popup);
    };
    const checkCondition = (
      condition: (memo: MemoDetail | ApprovalRequestList) => boolean,
    ): boolean => {
      return memos.some((memo) => memo && condition(memo));
    };

    let showDecisionPopup: DecitionPopup | null = null;

    const isS11Document = (memo: MemoDetail | ApprovalRequestList) =>
      ['s11_contract', 'other_reference'].includes(
        memo.memo_type as string,
      );
    const canSignContract =
      this.authenticationService.currentUserInfo?.can_sign_contract;

    const shouldShowOtpPopup = (memo: any) =>
      !pass('otp') &&
      !['pre_approve'].includes(memo?.memo_type as string) &&
      memo.otp_enable &&
      (!isS11Document(memo) ||
        (isS11Document(memo) && canSignContract));

    if (action === 'approve') {
      if (
        checkCondition(
          (memo): boolean =>
            memo.upload?.is_comment_required || false,
        ) &&
        !pass('comment')
      ) {
        showDecisionPopup = 'comment';
      } else if (
        checkCondition(
          (memo): boolean => memo.current_level_signature_required,
        ) &&
        !pass('signature')
      ) {
        showDecisionPopup = 'signature';
      } else if (checkCondition((memo) => shouldShowOtpPopup(memo))) {
        showDecisionPopup = 'otp';
      } else if (
        checkCondition(
          (memo): boolean =>
            !memo.current_level_signature_required &&
            (memo.memo_type === 'pre_approve' || !memo.otp_enable) &&
            !memo.upload?.is_comment_required,
        ) &&
        !pass('confirm')
      ) {
        showDecisionPopup = 'confirm';
      }
    } else if (action !== 'selfAssigned') {
      if (!pass('reason')) {
        showDecisionPopup = 'reason';
      } else if (
        checkCondition((memo) => shouldShowOtpPopup(memo)) &&
        action !== 'negotiate'
      ) {
        showDecisionPopup = 'otp';
      }
    }
    if (showDecisionPopup) {
      setState({
        ...state,
        showDecisionPopup,
        actionPayload: {
          ...actionPayload,
          is_user: payload?.options?.is_user,
        },
      });
    } else {
      const role = this.authenticationService.role.toLowerCase();
      const canSign =
        this.authenticationService.currentUserInfo.can_sign_contract;
      switch (action) {
        case 'approve':
          if (memo?.memo_type === 's11_contract') {
            if (
              role.includes('commander') &&
              memo?.current_level !== 1
            ) {
              const path =
                !canSign && memo?.current_level === 2
                  ? '/submit-payment-information/'
                  : '/commander-approve/';
              this.submitS11Contract(path, canSign);
            } else {
              this.dispatchByCondition(
                multiMemos,
                new MultiApprove(),
                new Approve(),
              );
            }
          } else if (
            memo?.memo_type === 'other_reference' &&
            role.includes('commander')
          ) {
            const path = '/commander-approve/';
            this.submitS11Contract(path, canSign);
          } else {
            this.dispatchByCondition(
              multiMemos,
              new MultiApprove(),
              new Approve(),
            );
          }
          break;
        case 'reject':
          multiMemos.length
            ? this.store.dispatch(new MultiReject())
            : this.store.dispatch(new Reject());
          break;
        case 'terminate':
          if (
            canSign &&
            state.isSelectAll &&
            actionPayload.action === 'terminate'
          ) {
            this.store.dispatch(new MultiTerminateS11Document());
          } else {
            multiMemos.length
              ? this.store.dispatch(new MultiTerminate())
              : this.store.dispatch(new Terminate());
          }
          break;
        case 'revise':
          this.verifyOtp();
          break;
        case 'selfAssigned':
          this.selfAssign();
          break;
        case 'negotiate':
          this.negotiate();
          break;
      }
    }
  }

  @Action(SaveComment)
  saveComment(
    { getState, setState }: StateContext<ApprovalRequestStateModel>,
    { comments }: { comments: string[] },
  ) {
    const state = getState();
    const passedDecisionPopups = state.passedDecisionPopups;
    const actionPayload = state.actionPayload;

    passedDecisionPopups.push('comment');
    actionPayload.comments = comments;

    setState({
      ...state,
      passedDecisionPopups,
      actionPayload,
    });
    this.store.dispatch(new ShowDecisionPopup());
  }

  @Action(SaveOTP)
  saveOTP(
    { getState, setState }: StateContext<ApprovalRequestStateModel>,
    { otp }: { otp: string },
  ) {
    const state = getState();
    const passedDecisionPopups = state.passedDecisionPopups;
    const actionPayload = state.actionPayload;

    passedDecisionPopups.push('otp');
    actionPayload.otp = otp;
    actionPayload.ref = this.store.selectSnapshot<string>(
      (s) => s.otp?.ref,
    );

    setState({
      ...state,
      passedDecisionPopups,
      actionPayload,
    });
    this.store.dispatch(new ShowDecisionPopup());
  }

  @Action(SaveReason)
  saveReason(
    { getState, setState }: StateContext<ApprovalRequestStateModel>,
    {
      reason,
    }: {
      reason: string | { negotiate_amount: number; reason: string };
    },
  ) {
    const state = getState();
    const passedDecisionPopups = state.passedDecisionPopups;
    const actionPayload = state.actionPayload;
    passedDecisionPopups.push('reason');

    if (actionPayload.action === 'negotiate') {
      actionPayload.negotiate = reason as {
        negotiate_amount: number;
        reason: string;
      };
    } else {
      actionPayload.reason = reason as string;
    }

    setState({
      ...state,
      passedDecisionPopups,
      actionPayload,
    });
    this.store.dispatch(new ShowDecisionPopup());
  }

  @Action(SaveSignature)
  saveSignature(
    { getState, setState }: StateContext<ApprovalRequestStateModel>,
    { signature }: { signature: File | null },
  ) {
    const state = getState();
    const passedDecisionPopups = [...state.passedDecisionPopups];

    if (
      signature !== null &&
      signature !== undefined &&
      !this.isFileSignature(signature)
    ) {
      this.warnInvalidSignature();
      return;
    }

    passedDecisionPopups.push('signature');

    setState({
      ...state,
      passedDecisionPopups,
      actionPayload: {
        ...state.actionPayload,
        signature: signature ?? null,
      },
    });
    this.store.dispatch(new ShowDecisionPopup());
  }

  @Action(ClearSignatureDraft)
  clearSignatureDraft({
    getState,
    setState,
  }: StateContext<ApprovalRequestStateModel>) {
    const state = getState();
    if (!state.actionPayload.signature) {
      return;
    }
    setState({
      ...state,
      actionPayload: { ...state.actionPayload, signature: null },
    });
  }

  @Action(SaveStamp)
  saveStamp(
    { getState, setState }: StateContext<ApprovalRequestStateModel>,
    { key, file, imageUrl }: SaveStamp,
  ) {
    const stamp = getState().actionPayload.stamp;
    const newStamp = { key, file, imageUrl };
    const idx = stamp.findIndex((item) => item.key === key);
    if (idx === -1) {
      setState(
        patch({
          actionPayload: patch({
            stamp: append([newStamp]),
          }),
        }),
      );
    } else {
      setState(
        patch({
          actionPayload: patch({
            stamp: updateItem(idx, newStamp),
          }),
        }),
      );
    }
  }

  @Action(SaveLocation)
  saveLocation(
    { setState }: StateContext<ApprovalRequestStateModel>,
    { location }: SaveLocation,
  ) {
    this.location = location;
  }

  @Action(SaveConfirm)
  saveConfirm({
    getState,
    setState,
  }: StateContext<ApprovalRequestStateModel>) {
    const state = getState();
    const passedDecisionPopups = state.passedDecisionPopups;
    passedDecisionPopups.push('confirm');
    setState({
      ...state,
      passedDecisionPopups,
    });
    this.store.dispatch(new ShowDecisionPopup());
  }

  @Action(CancelDecisionPopup)
  cancelDecisionPopup({
    getState,
    setState,
  }: StateContext<ApprovalRequestStateModel>) {
    const state = getState();
    setState({
      ...state,
      showDecisionPopup: null,
      passedDecisionPopups: [],
      actionPayload: defaultData.actionPayload,
    });
  }

  @Action(ClearState)
  clearState({ setState }: StateContext<ApprovalRequestStateModel>) {
    setState(defaultData);
  }

  @Action(Approve)
  approve({ getState }: StateContext<ApprovalRequestStateModel>) {
    const state = getState();
    if (!state.memo) {
      return;
    }

    const memoId = (state.memo as MemoDetail).id;
    const memoType = state.memo?.memo_type as string;
    const isFirstLevelLOA = state.memo.current_level === 1;
    const role = this.authenticationService.role.toLowerCase();
    const conditionApproveController =
      memoType !== 'pre_approve' ||
      (memoType === 'pre_approve' && !role.includes('controller'));

    this.spinnerService.show();

    const fd = this.prepareApproveFormData(state);
    if (!fd) {
      this.spinnerService.hide();
      return;
    }

    const isS11Type = [
      'pre_approve',
      's11_contract',
      'other_reference',
    ].includes(memoType);

    if (isS11Type) {
      if (state.actionPayload.is_user) {
        fd.append('is_user', state.actionPayload.is_user.toString());
      }

      // we will get `person_id` from LOA Level 1 only (since they contains Buyer and Guarantor)
      if (isFirstLevelLOA) {
        this.preApproveService
          .checkVerificationActionByType(memoId, memoType)
          .subscribe({
            next: (res) => {
              if (res?.person_id) {
                fd.append('person', res.person_id.toString());
              }
              this.executeApprove(
                memoId,
                fd,
                memoType,
                state,
                conditionApproveController,
                isS11Type,
              );
            },
            error: (error) => this.handleApproveError(error, true),
          });
      } else {
        // if it is other level, we can simply use normal logged in user to validate the action
        const currentUserPersonId =
          this.authenticationService.currentUserInfo?.id;
        if (currentUserPersonId) {
          fd.append('person', currentUserPersonId.toString());
        }
        this.executeApprove(
          memoId,
          fd,
          memoType,
          state,
          conditionApproveController,
          isS11Type,
        );
      }
    } else {
      this.executeApprove(
        memoId,
        fd,
        memoType,
        state,
        conditionApproveController,
        isS11Type,
      );
    }
  }

  private prepareApproveFormData(
    state: ApprovalRequestStateModel,
  ): FormData | null {
    const actionPayload = state.actionPayload;
    const fd = new FormData();
    if (actionPayload.comments?.length > 0) {
      actionPayload.comments.forEach((comment) => {
        fd.append('comments', comment);
      });
    }
    if (actionPayload.otp) {
      fd.append('otp', actionPayload.otp);
    }
    if (actionPayload.ref) {
      fd.append('ref', actionPayload.ref);
    }
    if (actionPayload.stamp?.length > 0) {
      actionPayload.stamp.forEach(({ key, file }) => {
        fd.append(key, file);
      });
    }
    if (this.location?.latitude && this.location?.longitude) {
      fd.append('latitude', this.location.latitude.toString());
      fd.append('longitude', this.location.longitude.toString());
    }
    if (!this.appendSignatureData(fd, actionPayload)) {
      return null;
    }

    return fd;
  }

  private handleApproveError(
    error: any,
    conditionApproveController: boolean,
  ) {
    this.spinnerService.hide();
    if (conditionApproveController) {
      this.store.dispatch(new ErrorNotification(error));
    }
    this.store.dispatch(new CancelDecisionPopup());
    this.store.dispatch(
      new SetOnApprove(
        conditionApproveController ? { error: true } : error,
      ),
    );
  }

  private executeApprove(
    memoId: number,
    fd: FormData,
    memoType: string,
    state: ApprovalRequestStateModel,
    conditionApproveController: boolean,
    isS11Type: boolean,
  ) {
    this.spinnerService.hide();
    fd.forEach((value, key) => {
      console.log(key, value);
    });
    const service = isS11Type
      ? this.preApproveService.approvePreApproveByType(
          memoId,
          fd,
          memoType,
        )
      : this.memoService.approveMemo(memoId, fd);

    service.subscribe({
      next: (res) => {
        this.handleActionSuccess(state, res);
        this.store.dispatch(new SetOnApprove(res));
      },
      error: (error) =>
        this.handleApproveError(error, conditionApproveController),
    });
  }

  @Action(Reject)
  reject({ getState }: StateContext<ApprovalRequestStateModel>) {
    const state = getState();
    this.spinnerService.show();
    const actionPayload = state.actionPayload;
    const data = {
      reason: actionPayload.reason,
      otp: actionPayload.otp,
      ref: actionPayload.ref,
    };
    this.memoService
      .rejectMemo((state.memo as MemoDetail).id, data)
      .pipe(finalize(() => this.spinnerService.hide()))
      .subscribe({
        next: () => {
          this.handleActionSuccess(state);
        },
        error: (error) => {
          this.store.dispatch(new ErrorNotification(error));
          this.store.dispatch(new CancelDecisionPopup());
        },
      });
  }

  @Action(Terminate)
  terminate({ getState }: StateContext<ApprovalRequestStateModel>) {
    const state = getState();
    this.spinnerService.show();
    const actionPayload = state.actionPayload;
    const data = {
      reason: actionPayload.reason,
      otp: actionPayload.otp,
      ref: actionPayload.ref,
    };
    const memoId = (state.memo as MemoDetail).id;
    const memoType = state.memo?.memo_type as string;
    const service = [
      'pre_approve',
      's11_contract',
      'other_reference',
    ].includes(memoType)
      ? this.preApproveService.terminatePreApproveByType(
          memoId,
          data,
          memoType,
        )
      : this.memoService.terminateMemo(memoId, data);
    service
      .pipe(finalize(() => this.spinnerService.hide()))
      .subscribe({
        next: (res) => {
          this.handleActionSuccess(state, res);
        },
        error: (error) => {
          this.store.dispatch(new ErrorNotification(error));
          this.store.dispatch(new CancelDecisionPopup());
        },
      });
  }

  @Action(MultiApprove)
  multiApprove({
    getState,
    patchState,
  }: StateContext<ApprovalRequestStateModel>) {
    const state = getState();
    this.spinnerService.show();
    const actionPayload = state.actionPayload;
    const memoType = state.memo?.memo_type.replace(
      /_/g,
      '-',
    ) as string;

    const fd = new FormData();
    const memoTypes = state.multiMemos.map((m) => m.memo_type);
    const allSameType = memoTypes.every((t) => t === memoTypes[0]);
    state.multiMemos.forEach((memo) => {
      fd.append('memos', memo.id.toString());
      if (!allSameType) {
        fd.append('memo_types', memo.memo_type.toString());
      }
    });
    if (allSameType && memoTypes.length > 0) {
      fd.append('memo_types', memoTypes[0].toString());
    }
    if (state.listQueryParams) {
      Object.entries(state.listQueryParams).forEach(
        ([key, value]) => {
          if (value != null && value !== '') {
            fd.set(key, String(value));
          }
        },
      );
    }
    if (actionPayload.comments && actionPayload.comments.length > 0) {
      actionPayload.comments.forEach((comment) => {
        fd.append('comments', comment);
      });
    }
    if (actionPayload.otp) {
      fd.set('otp', actionPayload.otp);
    }
    if (actionPayload.ref) {
      fd.set('ref', actionPayload.ref);
    }
    if (!this.appendSignatureData(fd, actionPayload)) {
      this.spinnerService.hide();
      return;
    }
    const service =
      memoType &&
      ['pre_approve', 's11_contract', 'other_reference'].includes(
        state.memo?.memo_type as string,
      )
        ? this.memoService.approveMultiByType(memoType, fd)
        : this.memoService.approveMultiMemos(fd);
    service
      .pipe(finalize(() => this.spinnerService.hide()))
      .subscribe({
        next: (res: { task_id: string }) => {
          const s11NotifyTypes = ['s11_contract', 'other_reference'];
          const showS11Done = s11NotifyTypes.includes(
            state.memo?.memo_type as string,
          );
          patchState({
            multiApproveS11Done: showS11Done,
            multiS11ActionType: showS11Done ? 'approve' : null,
            multiApprovalTaskId: showS11Done ? null : res.task_id,
            multiMemos: [],
            isSelectAll: false,
            selectAllMemoType: null,
            multiSelectedCount: 0,
            exceedMaximumApprove: false,
          });
          this.store.dispatch(new SetOnApprove(res));
        },
        error: (error: any) => {
          this.store.dispatch(new ErrorNotification(error));
          this.store.dispatch(new CancelDecisionPopup());
          this.store.dispatch(new SetOnApprove({ error: true }));
        },
      });
  }

  @Action(MultiApproveForS11Document)
  MultiApproveForS11Document({
    getState,
    patchState,
  }: StateContext<ApprovalRequestStateModel>) {
    const state = getState();
    this.spinnerService.show();
    const actionPayload = state.actionPayload;
    const fd = new FormData();
    const memoType = state.memo?.memo_type.replace(
      /_/g,
      '-',
    ) as string;
    this.appendLocation(fd);
    const selectAllMemoType =
      state.selectAllMemoType || state.memo?.memo_type;
    if (selectAllMemoType) {
      fd.append('memo_types', selectAllMemoType);
    }
    if (actionPayload.comments && actionPayload.comments.length > 0) {
      actionPayload.comments.forEach((comment) => {
        fd.append('comments', comment);
      });
    }
    if (actionPayload.otp) {
      fd.set('otp', actionPayload.otp);
    }
    if (actionPayload.ref) {
      fd.set('ref', actionPayload.ref);
    }
    if (state.listQueryParams) {
      const paginationKeys = new Set(['page', 'page_size']);
      Object.entries(state.listQueryParams).forEach(
        ([key, value]) => {
          if (state.isSelectAll && paginationKeys.has(key)) return;
          if (value != null && value !== '') {
            fd.set(key, String(value));
          }
        },
      );
    }
    if (!this.appendSignatureData(fd, actionPayload)) {
      this.spinnerService.hide();
      return;
    }
    this.memoService
      .approveMultiByType(memoType, fd)
      .pipe(finalize(() => this.spinnerService.hide()))
      .subscribe({
        next: (res: { task_id: string }) => {
          const s11NotifyTypes = ['s11_contract', 'other_reference'];
          const showS11Done = s11NotifyTypes.includes(
            state.memo?.memo_type as string,
          );
          patchState({
            isSelectAll: false,
            selectAllMemoType: null,
            multiMemos: [],
            multiSelectedCount: 0,
            multiApproveS11Done: showS11Done,
            multiS11ActionType: showS11Done ? 'approve' : null,
            multiApprovalTaskId: showS11Done ? null : res.task_id,
          });
          this.store.dispatch(new CancelDecisionPopup());
        },
        error: (error: any) => {
          this.store.dispatch(new ErrorNotification(error));
          this.store.dispatch(new CancelDecisionPopup());
          this.store.dispatch(new SetOnApprove({ error: true }));
        },
      });
  }

  @Action(SaveListQueryParams)
  saveListQueryParams(
    { patchState }: StateContext<ApprovalRequestStateModel>,
    action: SaveListQueryParams,
  ) {
    patchState({ listQueryParams: action.params });
  }

  @Action(SetMultiApproveS11Done)
  setMultiApproveS11Done(
    { patchState }: StateContext<ApprovalRequestStateModel>,
    action: SetMultiApproveS11Done,
  ) {
    const patch: Partial<ApprovalRequestStateModel> = {
      multiApproveS11Done: action.payload,
    };
    if (!action.payload) {
      patch.multiS11ActionType = null;
    }
    patchState(patch);
  }

  @Action(MultiReject)
  multiReject({
    getState,
    patchState,
  }: StateContext<ApprovalRequestStateModel>) {
    const state = getState();
    this.spinnerService.show();
    const actionPayload = state.actionPayload;
    const data = {
      memos: state.multiMemos.map((memo) => memo.id),
      reason: actionPayload.reason,
      otp: actionPayload.otp,
      ref: actionPayload.ref,
    };
    this.memoService
      .rejectMultiMemos(data)
      .pipe(finalize(() => this.spinnerService.hide()))
      .subscribe({
        next: (res: { task_id: string }) => {
          patchState({
            multiApprovalTaskId: res.task_id,
            multiMemos: [],
            isSelectAll: false,
            selectAllMemoType: null,
            multiSelectedCount: 0,
            exceedMaximumApprove: false,
          });
        },
        error: (error: any) => {
          patchState({
            multiMemos: [],
            isSelectAll: false,
            selectAllMemoType: null,
            multiSelectedCount: 0,
            exceedMaximumApprove: false,
          });
          this.store.dispatch(new ErrorNotification(error));
          this.store.dispatch(new CancelDecisionPopup());
        },
      });
  }

  @Action(MultiTerminate)
  multiTerminate({
    getState,
    patchState,
  }: StateContext<ApprovalRequestStateModel>) {
    const state = getState();
    this.spinnerService.show();
    const actionPayload = state.actionPayload;
    const memoType = state.memo?.memo_type.replace(
      /_/g,
      '-',
    ) as string;
    const isTypedTerminate =
      memoType &&
      ['pre_approve', 's11_contract', 'other_reference'].includes(
        state.memo?.memo_type as string,
      );
    let service;
    if (isTypedTerminate) {
      const fd = new FormData();
      const memoTypes = state.multiMemos.map((m) => m.memo_type);
      const allSameType = memoTypes.every((t) => t === memoTypes[0]);
      state.multiMemos.forEach((memo) => {
        fd.append('memos', memo.id.toString());
        if (!allSameType) {
          fd.append('memo_types', memo.memo_type.toString());
        }
      });
      if (allSameType && memoTypes.length > 0) {
        fd.append('memo_types', memoTypes[0].toString());
      }
      if (actionPayload.reason) {
        fd.set('reason', actionPayload.reason);
      }
      if (actionPayload.otp) {
        fd.set('otp', actionPayload.otp);
      }
      if (actionPayload.ref) {
        fd.set('ref', actionPayload.ref);
      }
      if (state.listQueryParams) {
        const paginationKeys = new Set(['page', 'page_size']);
        Object.entries(state.listQueryParams).forEach(
          ([key, value]) => {
            if (state.isSelectAll && paginationKeys.has(key)) return;
            if (value != null && value !== '') {
              fd.set(key, String(value));
            }
          },
        );
      }
      service = this.memoService.terminateMultiByType(memoType, fd);
    } else {
      const data = {
        memo_types: state.multiMemos.map((memo) => memo.memo_type),
        memo_type: memoType,
        memos: state.multiMemos.map((memo) => memo.id),
        reason: actionPayload.reason,
        otp: actionPayload.otp,
        ref: actionPayload.ref,
      };
      service = this.memoService.terminateMultiMemos(data);
    }
    service
      .pipe(finalize(() => this.spinnerService.hide()))
      .subscribe({
        next: (res: { task_id: string }) => {
          const s11NotifyTypes = ['s11_contract', 'other_reference'];
          const showS11Done = s11NotifyTypes.includes(
            state.memo?.memo_type as string,
          );
          patchState({
            multiApproveS11Done: showS11Done,
            multiS11ActionType: showS11Done ? 'terminate' : null,
            multiApprovalTaskId: showS11Done ? null : res.task_id,
            multiMemos: [],
            isSelectAll: false,
            selectAllMemoType: null,
            multiSelectedCount: 0,
            exceedMaximumApprove: false,
          });
        },
        error: (error: any) => {
          this.store.dispatch(new ErrorNotification(error));
          this.store.dispatch(new CancelDecisionPopup());
        },
      });
  }

  @Action(MultiTerminateS11Document)
  multiTerminateS11Document({
    getState,
    patchState,
  }: StateContext<ApprovalRequestStateModel>) {
    const state = getState();
    this.spinnerService.show();
    const actionPayload = state.actionPayload;
    const memoType = state.memo?.memo_type.replace(
      /_/g,
      '-',
    ) as string;
    const fd = new FormData();
    const selectAllMemoType =
      state.selectAllMemoType || state.memo?.memo_type;
    if (selectAllMemoType) {
      fd.append('memo_types', selectAllMemoType);
    }
    if (actionPayload.reason) {
      fd.set('reason', actionPayload.reason);
    }
    if (actionPayload.otp) {
      fd.set('otp', actionPayload.otp);
    }
    if (actionPayload.ref) {
      fd.set('ref', actionPayload.ref);
    }
    if (state.listQueryParams) {
      const paginationKeys = new Set(['page', 'page_size']);
      Object.entries(state.listQueryParams).forEach(
        ([key, value]) => {
          if (state.isSelectAll && paginationKeys.has(key)) return;
          if (value != null && value !== '') {
            fd.set(key, String(value));
          }
        },
      );
    }
    this.memoService
      .terminateMultiByType(memoType, fd)
      .pipe(finalize(() => this.spinnerService.hide()))
      .subscribe({
        next: (res: { task_id: string }) => {
          const s11NotifyTypes = ['s11_contract', 'other_reference'];
          const showS11Done = s11NotifyTypes.includes(
            state.memo?.memo_type as string,
          );
          patchState({
            isSelectAll: false,
            selectAllMemoType: null,
            multiMemos: [],
            multiSelectedCount: 0,
            multiApproveS11Done: showS11Done,
            multiS11ActionType: showS11Done ? 'terminate' : null,
            multiApprovalTaskId: showS11Done ? null : res.task_id,
          });
          this.store.dispatch(new CancelDecisionPopup());
        },
        error: (error: any) => {
          this.store.dispatch(new ErrorNotification(error));
          this.store.dispatch(new CancelDecisionPopup());
        },
      });
  }

  private verifyOtp(): void {
    const state =
      this.store.selectSnapshot<ApprovalRequestStateModel>(
        (s) => s.approvalRequest,
      );
    if (!state?.memo) return;
    const { otp, ref } = state.actionPayload || {};
    if (!otp || !ref) return;

    this.otpSmsService.verify({ otp, ref }).subscribe({
      error: (error: any) => {
        this.store.dispatch(new ErrorNotification(error));
        this.store.dispatch(new CancelDecisionPopup());
      },
      complete: () => this.onRevise(),
    });
  }

  onRevise(): void {
    const state =
      this.store.selectSnapshot<ApprovalRequestStateModel>(
        (s) => s.approvalRequest,
      );
    if (!state?.memo) return;
    const memoId = (state.memo as MemoDetail).id;
    const data = {
      reason: state.actionPayload?.reason,
    } as { reason: string };

    this.spinnerService.show();
    this.preApproveService
      .commanderRevise(memoId, data)
      .pipe(finalize(() => this.spinnerService.hide()))
      .subscribe({
        next: () => {
          this.notificationService.updateNotificationCount();
          this.store.dispatch(new NextApprovalPopup('show'));
        },
        error: (error) => {
          this.store.dispatch(new ErrorNotification(error));
          this.store.dispatch(new CancelDecisionPopup());
        },
      });
  }

  private selfAssign() {
    const state =
      this.store.selectSnapshot<ApprovalRequestStateModel>(
        (state) => state.approvalRequest,
      );
    if (!state.memo) return;

    const memoId = (state.memo as MemoDetail).id;
    this.spinnerService.show();

    this.preApproveService
      .selfAssign(memoId)
      .pipe(finalize(() => this.spinnerService.hide()))
      .subscribe({
        next: () => {
          this.notificationService.updateNotificationCount();
          this.store.dispatch(new NextApprovalPopup('show'));
        },
        error: (error) => {
          this.store.dispatch(new ErrorNotification(error));
          this.store.dispatch(new CancelDecisionPopup());
        },
      });
  }

  private dispatchByCondition(
    conditionList: any[],
    multiAction: any,
    singleAction: any,
  ) {
    this.store.dispatch(
      conditionList.length ? multiAction : singleAction,
    );
  }

  private negotiate() {
    const state =
      this.store.selectSnapshot<ApprovalRequestStateModel>(
        (state) => state.approvalRequest,
      );
    if (!state.memo) return;

    const memoId = (state.memo as MemoDetail).id;
    const data = state.actionPayload;
    if (data?.negotiate) {
      this.spinnerService.show();

      this.preApproveService
        .negotiate(memoId, data.negotiate)
        .pipe(finalize(() => this.spinnerService.hide()))
        .subscribe({
          next: () => {
            this.notificationService.updateNotificationCount();
            this.store.dispatch(
              new NextApprovalPopup('show', 'success'),
            );
          },
          error: (error) => {
            this.store.dispatch(
              new NextApprovalPopup('show', 'error'),
            );
          },
        });
    }
  }

  private submitS11Contract(pathUrl: string, isCheckSign = false) {
    const state =
      this.store.selectSnapshot<ApprovalRequestStateModel>(
        (state) => state.approvalRequest,
      );
    const actionPayload = state.actionPayload;
    const location = this.location;
    const memoId = (state.memo as MemoDetail).id;
    const submitData =
      state.memo?.memo_type === 's11_contract'
        ? this.prepareSubmitDataS11Contract(isCheckSign, state)
        : this.prepareSubmitOtherReference(state);
    if (!submitData) {
      this.store.dispatch(new CancelDecisionPopup());
      return;
    }
    const isEmpty = this.isFormDataEmpty(submitData);
    if (isEmpty || !(location?.latitude && location?.longitude)) {
      if (!location?.latitude || !location?.longitude) {
        this.alert.error(
          'ไม่สามารถค้นหาตำแหน่งได้ในขณะนี้ โปรดลองใหม่ภายหลัง',
        );
      }
      this.store.dispatch(new CancelDecisionPopup());
      return;
    }
    if (
      state.isSelectAll &&
      isCheckSign &&
      actionPayload.action === 'approve'
    ) {
      this.store.dispatch(new MultiApproveForS11Document());
    } else {
      const multiMemos = state.multiMemos;
      if (multiMemos.length) {
        this.store.dispatch(new MultiApprove());
        return;
      }
      const apiUrl =
        state.memo?.memo_type === 's11_contract'
          ? this.preApproveService.submitS11Contract(
              memoId,
              submitData,
              pathUrl,
            )
          : this.preApproveService.submitOtherReferenceContract(
              memoId,
              submitData,
              pathUrl,
            );
      this.spinnerService.show();
      apiUrl
        .pipe(finalize(() => this.spinnerService.hide()))
        .subscribe({
          next: (res: any) => {
            if (res?.render_task_id) {
              this.store.dispatch(
                new SetBackgroundTask(
                  res.render_task_id,
                  'EXPORT.PROCESSING-DOCUMENT',
                ),
              );
              this.store.dispatch(new SetOnApprove(res));
              return;
            }
            this.notificationService.updateNotificationCount();
            this.store.dispatch(
              new NextApprovalPopup('show', 'success'),
            );
            this.store.dispatch(new ClearSelectMemo(false));
          },
          error: (error) => {
            this.store.dispatch(new ErrorNotification(error));
            this.store.dispatch(new CancelDecisionPopup());
          },
        });
    }
  }

  private isFormDataEmpty(fd?: FormData | null): boolean {
    if (!fd) return true;
    let empty = true;
    fd.forEach(() => {
      empty = false;
    });
    return empty;
  }

  private appendLocation(fd: FormData): void {
    if (this.location?.latitude) {
      fd.append('latitude', String(this.location.latitude));
    }
    if (this.location?.longitude) {
      fd.append('longitude', String(this.location.longitude));
    }
  }

  private isFileSignature(signature: unknown): signature is File {
    return typeof File !== 'undefined' && signature instanceof File;
  }

  private warnInvalidSignature(): void {
    this.alert.warning(
      'ลายเซ็นไม่ถูกต้อง',
      'กรุณาเลือกไฟล์รูปภาพลายเซ็นใหม่อีกครั้ง',
    );
  }

  private appendSignatureData(fd: FormData, payload: any): boolean {
    const sig = payload.signature;
    if (sig === null || sig === undefined) {
      return true;
    }

    if (!this.isFileSignature(sig)) {
      this.warnInvalidSignature();
      return false;
    }

    fd.append('one_time_signature', sig, sig.name);
    return true;
  }

  private appendContractData(fd: FormData, data: any): void {
    for (const key in data) {
      const raw = data[key];
      if (raw === null || raw === undefined) {
        fd.append(key, '');
        continue;
      }
      if (typeof raw === 'number') {
        fd.append(key, String(raw));
        continue;
      }
      if (typeof raw === 'string') {
        const trimmed = raw.trim();

        if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
          fd.append(key, trimmed);
        } else {
          fd.append(key, raw);
        }
        continue;
      }
      fd.append(key, String(raw));
    }
  }

  private prepareSubmitDataS11Contract(
    isCheckSign: boolean,
    state: ApprovalRequestStateModel,
  ): FormData | null {
    const fd = new FormData();
    const payload = state.actionPayload;

    if (isCheckSign) {
      this.appendLocation(fd);
      if (payload.otp) fd.append('otp', payload.otp);
      if (payload.ref) fd.append('ref', payload.ref);
      if (!this.appendSignatureData(fd, payload)) {
        return null;
      }

      return fd;
    }

    if (!state.s11ContractData) return null;

    this.appendLocation(fd);
    if (!this.appendSignatureData(fd, payload)) {
      return null;
    }
    this.appendContractData(fd, state.s11ContractData);

    return fd;
  }

  private prepareSubmitOtherReference(
    state: ApprovalRequestStateModel,
  ): FormData | null {
    const actionPayload = state.actionPayload;
    const fd = new FormData();
    if (state.memo?.current_level) {
      fd.append(
        'commander_level',
        state.memo.current_level.toString(),
      );
    }

    if (actionPayload.otp) {
      fd.append('otp', actionPayload.otp);
    }
    if (actionPayload.ref) {
      fd.append('ref', actionPayload.ref);
    }
    if (!this.appendSignatureData(fd, actionPayload)) {
      return null;
    }
    this.appendLocation(fd);
    return fd;
  }

  @Action(NextApprovalPopup)
  nextApprovalPopup(
    { patchState }: StateContext<ApprovalRequestStateModel>,
    { state, status }: NextApprovalPopup,
  ) {
    patchState({
      nextApprovalPopup: state,
      multiApprovalTaskId: null,
      multiApprovalTaskTitle: null,
      multiMemos: [],
      exceedMaximumApprove: false,
      selectedType: null,
      selectAllMemoType: null,
      statusFetch: status || null,
    });
  }

  @Action(ShowPreviewData)
  showPreviewData(
    { patchState }: StateContext<ApprovalRequestStateModel>,
    { payload }: { payload: PreviewData },
  ) {
    patchState({
      previewData: payload,
    });
  }

  @Action(ClosePreviewData)
  closePreviewData({
    patchState,
  }: StateContext<ApprovalRequestStateModel>) {
    patchState({
      previewData: defaultData.previewData,
    });
  }

  @Action(EmptyContent)
  emptyContent(
    { patchState }: StateContext<ApprovalRequestStateModel>,
    { emptyContent }: { emptyContent?: boolean },
  ) {
    patchState({
      emptyContent: emptyContent,
    });
  }

  @Action(SetIsSelectAll)
  setIsSelectAll(
    { patchState }: StateContext<ApprovalRequestStateModel>,
    { isSelectAll, memoType }: SetIsSelectAll,
  ) {
    patchState({
      isSelectAll,
      selectAllMemoType: memoType ?? null,
    });
  }

  @Action(UpdateMultiMemos)
  updateMultiMemos(
    { patchState }: StateContext<ApprovalRequestStateModel>,
    { payload }: { payload: ApprovalRequestList[] },
  ) {
    const MAXIMUM_MULTI_APPROVE = 10;
    const selectedType = this.checkSelectedMemoType(payload);
    const unlimitedTypes = new Set([
      'pre_approve',
      's11_contract',
      'other_reference',
    ]);
    const exceedMaximumApprove = unlimitedTypes.has(
      selectedType as string,
    )
      ? false
      : payload.length >= MAXIMUM_MULTI_APPROVE;
    patchState({
      multiMemos: payload,
      exceedMaximumApprove: exceedMaximumApprove,
      selectedType: selectedType,
    });
  }

  handleActionSuccess(
    state: ApprovalRequestStateModel,
    memoRes?: MemoDetail,
  ) {
    if (state.viaLink) {
      if (memoRes) {
        this.store.dispatch(
          new InitialMemoDetail(memoRes, state.viaLink),
        );
      } else {
        this.store.dispatch(
          new ShowMemoDetail((state.memo as MemoDetail).id, true),
        );
      }
    } else {
      this.notificationService.updateNotificationCount();
      this.sidebarService.fetchBadge();
      this.store.dispatch(new NextApprovalPopup('show'));
    }
    this.spinnerService.hide();
  }

  checkSelectedMemoType(
    memos: ApprovalRequestList[],
  ):
    | 'memo'
    | 'contract'
    | 'pre_approve'
    | 's11_contract'
    | 'other_reference'
    | 'e_poa'
    | null {
    if (!memos?.length) {
      return null;
    }

    const reservedTypes = new Set([
      'contract',
      'pre_approve',
      's11_contract',
      'other_reference',
      'e_poa',
    ]);

    const types = new Set(
      memos.map((m) => (m.memo_type as string) || ''),
    );

    if (types.size === 1) {
      const onlyType = Array.from(types)[0];
      if (reservedTypes.has(onlyType)) {
        return onlyType as
          | 'contract'
          | 'pre_approve'
          | 's11_contract'
          | 'other_reference'
          | 'e_poa';
      }
      return 'memo';
    }

    const hasReserved = Array.from(types).some((t) =>
      reservedTypes.has(t),
    );
    if (!hasReserved) return 'memo';

    throw new Error('Not allow to selected both memo and contract');
  }

  private updateState(
    ctx: StateContext<ApprovalRequestStateModel>,
    updates: Partial<ApprovalRequestStateModel>,
  ) {
    ctx.patchState(updates);
  }

  @Action(SetFromClickMemo)
  setFromClickMemo(
    ctx: StateContext<ApprovalRequestStateModel>,
    { fromClickMemo }: SetFromClickMemo,
  ) {
    this.updateState(ctx, { fromClickMemo });
  }

  @Action(SaveFormDataAndStatus)
  saveFormDataAndStatus(
    ctx: StateContext<ApprovalRequestStateModel>,
    { s11ContractData, isValid }: SaveFormDataAndStatus,
  ) {
    this.updateState(ctx, {
      s11ContractData: s11ContractData as PaymentData,
      s11ContractFormValid: isValid,
    });
  }

  @Action(UpdateMultiSelectedCount)
  updateMultiSelectedCount(
    ctx: StateContext<ApprovalRequestStateModel>,
    { count }: UpdateMultiSelectedCount,
  ) {
    this.updateState(ctx, {
      multiSelectedCount: count,
    });
  }
}

export class ApprovalRequestSelectors {
  static getSlices =
    createPropertySelectors<ApprovalRequestStateModel>(
      ApprovalRequestState,
    );

  static getBasicSlices = createModelSelector({
    showingMenu: ApprovalRequestSelectors.getSlices.showingMenu,
    nextApprovalPopup:
      ApprovalRequestSelectors.getSlices.nextApprovalPopup,
    emptyContent: ApprovalRequestSelectors.getSlices.emptyContent,
  });
}
